/* Campaign Tools modal — Phase 4 of the Campaign Manager.
 *
 * Consolidates every tool that used to live on the legacy Crisis
 * Response tab into one panel accessible from the Campaign Manager
 * row menu:
 *   - Download PDF transparency report
 *   - Download CSV export
 *   - Distributions log (list + add + remove)
 *   - Donations list (read-only)
 *   - Send transparency report to donors (subject + optional intro)
 *
 * All operations continue to hit the legacy `/api/admin/crisis-campaigns/*`
 * router — which shares the same MongoDB collection — so donation
 * history and PDF/CSV outputs remain byte-for-byte identical. The UI
 * skin is simpler than the legacy 4-step stepper: one scroll, four
 * clear sections.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Download, Send, X, Trash2, Loader2, Package, DollarSign, Users, Wallet,
  Check, Plus, FileText, Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Legacy router — same MongoDB collection as /api/admin/donation-campaigns.
// We keep hitting it for the historical PDF/CSV/distributions/send-report
// endpoints since those are proven, tested, and still power everything.
const LEGACY_API = `${BACKEND_URL}/api/admin/crisis-campaigns`;

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toLocaleDateString(); } catch { return v; } };

const MetricCard = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded p-2.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-[#6B7280] flex items-center gap-1 mb-0.5">
      {Icon && <Icon className="w-3 h-3" />}{label}
    </div>
    <p className="font-heading font-bold text-sm text-[#0A0A0A]">{value}</p>
  </div>
);

const CampaignToolsModal = ({ campaign, onClose, onUpdated }) => {
  const [c, setC] = useState(campaign);
  const [donations, setDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(true);

  // Distributions form
  const [distForm, setDistForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '', recipient: '', description: '',
  });
  const [distSaving, setDistSaving] = useState(false);

  // Send report form
  const [subject, setSubject] = useState(`Transparency Report — ${campaign.name}`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentSummary, setSentSummary] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState({});
  const [recipientsLoading, setRecipientsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${LEGACY_API}/${campaign.id}`, { withCredentials: true });
      setC(r.data);
      onUpdated?.(r.data);
    } catch { /* silent */ }
  }, [campaign.id, onUpdated]);

  const loadDonations = useCallback(async () => {
    setDonationsLoading(true);
    try {
      const r = await axios.get(`${LEGACY_API}/${campaign.id}/donations`,
        { withCredentials: true });
      setDonations(r.data?.donations || []);
    } catch { setDonations([]); }
    setDonationsLoading(false);
  }, [campaign.id]);

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try {
      const r = await axios.get(`${LEGACY_API}/${campaign.id}/recipients`,
        { withCredentials: true });
      const list = r.data?.recipients || [];
      setRecipients(list);
      // Default: all selected
      const sel = {}; list.forEach((x) => { sel[x.email] = true; });
      setSelectedEmails(sel);
    } catch { setRecipients([]); }
    setRecipientsLoading(false);
  }, [campaign.id]);

  useEffect(() => { loadDonations(); loadRecipients(); }, [loadDonations, loadRecipients]);

  const downloadPdf = () => {
    window.open(`${LEGACY_API}/${c.id}/report.pdf`, '_blank');
  };
  const downloadCsv = () => {
    window.open(`${LEGACY_API}/${c.id}/export.csv`, '_blank');
  };

  const addDistribution = async () => {
    const amt = parseFloat(distForm.amount);
    if (!amt || amt <= 0) { alert('Amount must be greater than 0'); return; }
    setDistSaving(true);
    try {
      await axios.post(`${LEGACY_API}/${c.id}/distributions`, {
        date: new Date(distForm.date).toISOString(),
        amount: amt,
        recipient: distForm.recipient,
        description: distForm.description,
      }, { withCredentials: true });
      setDistForm({ date: new Date().toISOString().slice(0, 10),
                    amount: '', recipient: '', description: '' });
      await refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add distribution'); }
    setDistSaving(false);
  };

  const removeDistribution = async (distId) => {
    if (!window.confirm('Remove this distribution entry? '
                        + 'This does not affect the associated donations.')) return;
    try {
      await axios.delete(`${LEGACY_API}/${c.id}/distributions/${distId}`,
        { withCredentials: true });
      await refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Remove failed'); }
  };

  const emails = recipients.filter((r) => selectedEmails[r.email]).map((r) => r.email);
  const toggleEmail = (e) => setSelectedEmails((s) => ({ ...s, [e]: !s[e] }));
  const setAll = (v) => setSelectedEmails(
    Object.fromEntries(recipients.map((r) => [r.email, v]))
  );

  const sendReport = async () => {
    if (emails.length === 0) { alert('Select at least one recipient.'); return; }
    if (!window.confirm(`Send transparency report to ${emails.length} donor(s)?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${LEGACY_API}/${c.id}/send-report`,
        { recipients: emails, subject, message },
        { withCredentials: true });
      setSentSummary(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Send failed.');
    }
    setSending(false);
  };

  const distributions = (c.distributions || []).slice().reverse();
  const dists_count = distributions.length;

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="campaign-tools-modal">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wide font-bold text-[#6B7280]">Campaign Tools</p>
            <h3 className="font-heading font-bold text-lg">{c.name}</h3>
          </div>
          <button onClick={onClose} data-testid="tools-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {/* Metrics summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <MetricCard label="Raised" value={money(c.total_raised)} icon={DollarSign}
                      testid="tools-raised" />
          <MetricCard label="Donors" value={c.donor_count || 0} icon={Users}
                      testid="tools-donors" />
          <MetricCard label="Distributed" value={money(c.distributed_amount)} icon={Package}
                      testid="tools-distributed" />
          <MetricCard label="Balance" value={money(c.remaining_balance)} icon={Wallet}
                      testid="tools-balance" />
        </div>

        {/* Section 1 — Downloads */}
        <section className="mb-5 pb-5 border-b border-[#E5E7EB]" data-testid="tools-downloads-section">
          <h4 className="font-heading font-bold text-sm text-[#0A0A0A] mb-1 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#BA1826]" /> Downloads
          </h4>
          <p className="text-xs text-[#6B7280] mb-2">
            Grab the branded transparency PDF or a raw CSV of every paid donation and distribution.
          </p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadPdf}
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              data-testid="tools-download-pdf-btn">
              <Download className="w-4 h-4" /> Download PDF Report
            </button>
            <button onClick={downloadCsv}
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              data-testid="tools-download-csv-btn">
              <Download className="w-4 h-4" /> Download CSV
            </button>
          </div>
        </section>

        {/* Section 2 — Distributions */}
        <section className="mb-5 pb-5 border-b border-[#E5E7EB]" data-testid="tools-distributions-section">
          <h4 className="font-heading font-bold text-sm text-[#0A0A0A] mb-1 flex items-center gap-2">
            <Package className="w-4 h-4 text-[#BA1826]" /> Distributions
            <span className="text-xs font-normal text-[#6B7280]">
              · {dists_count} logged · {money(c.distributed_amount)} total
            </span>
          </h4>
          <p className="text-xs text-[#6B7280] mb-3">
            Track disbursements out of this campaign. Each entry updates the remaining balance.
          </p>

          {/* Add form */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
            <input type="date" value={distForm.date}
              onChange={(e) => setDistForm({...distForm, date: e.target.value})}
              className="px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs"
              data-testid="tools-dist-date" />
            <input value={distForm.recipient}
              onChange={(e) => setDistForm({...distForm, recipient: e.target.value})}
              placeholder="Recipient / area"
              className="px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs md:col-span-1"
              data-testid="tools-dist-recipient" />
            <input type="number" step="0.01" min="0" value={distForm.amount}
              onChange={(e) => setDistForm({...distForm, amount: e.target.value})}
              placeholder="Amount"
              className="px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs"
              data-testid="tools-dist-amount" />
            <input value={distForm.description}
              onChange={(e) => setDistForm({...distForm, description: e.target.value})}
              placeholder="Description"
              className="px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-xs"
              data-testid="tools-dist-desc" />
            <button onClick={addDistribution} disabled={distSaving}
              className="btn-primary text-xs inline-flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="tools-dist-add-btn">
              {distSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Add
            </button>
          </div>

          {/* List */}
          {distributions.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] italic py-3 text-center">
              No distributions logged yet.
            </p>
          ) : (
            <div className="border border-[#E5E7EB] rounded max-h-56 overflow-y-auto">
              <table className="w-full text-xs" data-testid="tools-distributions-table">
                <thead className="bg-[#F9FAFB] text-[#4B5563] uppercase text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Recipient</th>
                    <th className="p-2 text-right">Amount</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {distributions.map((d) => (
                    <tr key={d.id} className="border-t border-[#F1F5F9]"
                        data-testid={`tools-dist-row-${d.id}`}>
                      <td className="p-2">{fmtDate(d.date)}</td>
                      <td className="p-2">{d.recipient || '—'}</td>
                      <td className="p-2 text-right font-medium">{money(d.amount)}</td>
                      <td className="p-2 text-[#4B5563]">{d.description || '—'}</td>
                      <td className="p-2">
                        <button onClick={() => removeDistribution(d.id)}
                          className="text-[#B91C1C] hover:bg-red-50 p-1 rounded"
                          data-testid={`tools-dist-remove-${d.id}`}
                          title="Remove distribution">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Section 3 — Donations list */}
        <section className="mb-5 pb-5 border-b border-[#E5E7EB]" data-testid="tools-donations-section">
          <h4 className="font-heading font-bold text-sm text-[#0A0A0A] mb-1 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#BA1826]" /> Donations attributed to this campaign
            <span className="text-xs font-normal text-[#6B7280]">
              · {donations.length} shown
            </span>
          </h4>
          {donationsLoading ? (
            <p className="text-xs text-[#6B7280] py-3"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading…</p>
          ) : donations.length === 0 ? (
            <p className="text-xs text-[#9CA3AF] italic py-3 text-center">
              No donations recorded for this campaign yet.
            </p>
          ) : (
            <div className="border border-[#E5E7EB] rounded max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F9FAFB] text-[#4B5563] uppercase text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Donor</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.id || d.session_id} className="border-t border-[#F1F5F9]">
                      <td className="p-2">{fmtDate(d.created_at || d.paid_at)}</td>
                      <td className="p-2">{d.donor_name || <span className="text-[#9CA3AF]">Anonymous</span>}</td>
                      <td className="p-2 text-right font-medium">{money(d.amount || d.total_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Section 4 — Send Transparency Report */}
        <section data-testid="tools-send-report-section">
          <h4 className="font-heading font-bold text-sm text-[#0A0A0A] mb-1 flex items-center gap-2">
            <Send className="w-4 h-4 text-[#BA1826]" /> Send Transparency Report
          </h4>
          <p className="text-xs text-[#6B7280] mb-3">
            Emails every selected donor the branded PDF + CSV as attachments.
          </p>

          {sentSummary ? (
            <div className="text-center py-6" data-testid="tools-send-done">
              <Check className="w-10 h-10 text-[#22C55E] mx-auto mb-2" />
              <p className="font-heading font-bold text-base mb-1">
                Sent to {sentSummary.sent} donor{sentSummary.sent === 1 ? '' : 's'}
              </p>
              {sentSummary.failed > 0 && (
                <p className="text-xs text-[#B91C1C]">
                  {sentSummary.failed} address{sentSummary.failed === 1 ? '' : 'es'} failed — see the campaign detail.
                </p>
              )}
              <button onClick={() => { setSentSummary(null); refresh(); }}
                className="btn-secondary text-xs mt-3"
                data-testid="tools-send-again-btn">
                Send again
              </button>
            </div>
          ) : (
            <>
              <label className="block text-xs font-medium text-[#4B5563] mb-1">Email subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm mb-3"
                data-testid="tools-email-subject" />

              <label className="block text-xs font-medium text-[#4B5563] mb-1">
                Optional personal intro (appears above the metrics)
              </label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                rows={2} placeholder="Leave blank to use the default transparency message."
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm mb-3"
                data-testid="tools-email-message" />

              <div className="flex items-center justify-between mb-2">
                <p className="text-xs">
                  <b>{emails.length}</b> of <b>{recipients.length}</b> donors selected.
                </p>
                <div className="flex gap-2 text-xs">
                  <button onClick={() => setAll(true)}
                    className="text-[#BA1826] hover:underline"
                    data-testid="tools-select-all">Select all</button>
                  <button onClick={() => setAll(false)}
                    className="text-[#4B5563] hover:underline"
                    data-testid="tools-select-none">Deselect all</button>
                </div>
              </div>

              {recipientsLoading ? (
                <p className="text-xs text-[#6B7280] py-3">
                  <Loader2 className="w-3 h-3 inline animate-spin" /> Loading donors…
                </p>
              ) : recipients.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] italic py-3 text-center border border-dashed border-[#E5E7EB] rounded"
                   data-testid="tools-no-recipients">
                  No donors with email addresses are linked to this campaign yet.
                </p>
              ) : (
                <div className="border border-[#E5E7EB] rounded max-h-40 overflow-y-auto mb-3">
                  <table className="w-full text-xs">
                    <tbody>
                      {recipients.map((r) => (
                        <tr key={r.email} className="border-t border-[#F1F5F9]"
                            data-testid={`tools-recipient-${r.email}`}>
                          <td className="p-1.5 w-6">
                            <input type="checkbox"
                              checked={!!selectedEmails[r.email]}
                              onChange={() => toggleEmail(r.email)}
                              className="accent-[#BA1826]"
                              data-testid={`tools-recipient-check-${r.email}`} />
                          </td>
                          <td className="p-1.5">{r.name}</td>
                          <td className="p-1.5 text-[#4B5563]">{r.email}</td>
                          <td className="p-1.5 text-right text-[#4B5563]">{money(r.last_donation_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <button onClick={sendReport} disabled={sending || emails.length === 0}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="tools-send-report-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Send to {emails.length} donor{emails.length === 1 ? '' : 's'}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default CampaignToolsModal;
